import { useEffect, useRef, useState } from "react";
import type { TimelineDocument } from "@heroncendre/schema";
import { selectGuideTrack, trackDuration, useGuidedOptional, useTourStoreApi } from "@heroncendre/player";
import type { SortieGuide } from "./guide/useGuide.js";
import { GuideInterface } from "./guide/GuideInterface.js";
import { storeProgression } from "./guide/guideProgress.js";
import { pointDeReprise, repriseProposable, type PointDeReprise } from "./guide/progression.js";
import { ChoixReprise } from "./guide/ChoixReprise.js";

/**
 * ============================================================================
 *  ParcoursGuide.tsx — Lancer un parcours choisi à l'accueil
 * ----------------------------------------------------------------------------
 *  Le clic sur la carte a déjà amorcé l'audio (voir `Accueil`). Ce composant
 *  démarre le parcours dès que le player le tient : la carte peut désigner un
 *  parcours qui n'est pas celui que le player avait à l'accueil, auquel cas le
 *  pilote ne change qu'au rendu suivant.
 *
 *  L'interface du mode guidé (`guide/GuideInterface`) n'est montée qu'APRÈS le
 *  démarrage : sinon une image d'écran vide précéderait le parcours.
 *
 *  Si le visiteur a déjà vu une partie du parcours (`repriseProposable`), il choisit :
 *  reprendre là où il s’était arrêté, ou recommencer du début (`ChoixReprise`). Rien ne
 *  démarre avant sa réponse ; sans progression à reprendre, le parcours part tout de suite.
 *
 *  Une fois lancé, c'est elle qui conduit : bandes, barre de la main, fin de
 *  chapitre, retour à la visite libre. Elle remplace l'interface du moteur.
 * ==========================================================================*/

export function ParcoursGuide({
  timeline,
  guideId,
  surSortie,
  surAttente,
}: {
  timeline: TimelineDocument;
  /** Le guide (`mariage`) dont on mémorise la progression, et dont on reprend le fil. */
  guideId?: string;
  surSortie?: (vers: SortieGuide) => void;
  /** Prévient l’hôte tant que le visiteur doit choisir (le noir de l’accueil ne doit pas se lever). */
  surAttente?: (enAttente: boolean) => void;
}) {
  const store = useTourStoreApi();
  const guide = useGuidedOptional();
  const lance = useRef<TimelineDocument | null>(null);
  const [demarre, setDemarre] = useState(false);
  /** Le point de reprise proposé, et le nombre de chapitres : tant qu’il est là, la question est posée. */
  const [question, setQuestion] = useState<{ reprise: PointDeReprise; total: number } | null>(null);

  /** Lance le parcours au point donné (`null` : du début), puis monte l’interface. */
  const demarrer = (reprise: PointDeReprise | null) => {
    if (!guide) return;
    store.getState().startGuided(guide.pilot, reprise?.index ?? 0);
    if (reprise && reprise.t > 0) {
      // Le saut n’a de sens qu’une fois la piste du chapitre chargée : la durée connue en est le
      // signe. Demandé avant, il serait écrasé par le chargement.
      const t = reprise.t;
      let fait = false;
      const arreter = store.subscribe((etat) => {
        if (fait || etat.narration.duration <= 0) return;
        fait = true;
        arreter();
        etat.seekNarration(t);
      });
      window.setTimeout(arreter, 15000);
    }
    setDemarre(true);
  };

  useEffect(() => {
    // Le lecteur doit tenir CE parcours : tant que le player est sur un autre, on attend.
    if (!guide || guide.timeline !== timeline) return;
    // Une seule fois par parcours, même en rendu double du mode strict.
    if (lance.current === timeline) return;
    lance.current = timeline;
    // Le visiteur revient : où s’était-il arrêté (chapitre, puis instant de la voix) ? Sans enregistrement,
    // ou sans identifiant (`?demo`), on part du début.
    const { tour, context } = store.getState();
    const etapes = guide.timeline.steps;
    const reprise = guideId
      ? pointDeReprise(
          storeProgression(guideId).lire().last,
          etapes.map((etape) => {
            const piste = selectGuideTrack(tour, etape.guideId, context.language)?.track;
            return { id: etape.id, duree: (piste && trackDuration(piste)) || 0, scenes: 0 };
          }),
        )
      : null;
    // Un début à peine entamé ne vaut pas une question : on repart du début.
    if (repriseProposable(reprise)) setQuestion({ reprise, total: etapes.length });
    else demarrer(null);
    // `demarrer` ne dépend que de ce qui est déjà listé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide, timeline, store, guideId]);

  useEffect(() => {
    surAttente?.(question !== null);
    return () => surAttente?.(false);
  }, [question, surAttente]);

  if (question) {
    const choisir = (reprise: PointDeReprise | null) => {
      setQuestion(null);
      demarrer(reprise);
    };
    return (
      <ChoixReprise
        chapitre={question.reprise.index + 1}
        total={question.total}
        surReprendre={() => choisir(question.reprise)}
        surRecommencer={() => choisir(null)}
      />
    );
  }

  return demarre ? <GuideInterface guideId={guideId} surSortie={surSortie} /> : null;
}
